import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// CSS for the AI Orb animation
const orbStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=DM+Sans:wght@400;500&display=swap');
  
  @keyframes orbRotate {
    0% { transform: rotateX(-20deg) rotateY(0deg); }
    100% { transform: rotateX(-20deg) rotateY(360deg); }
  }
  
  @keyframes orbPulse {
    0%, 100% { opacity: 0.8; }
    50% { opacity: 1; }
  }
  
  @keyframes orbFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .orb-container {
    perspective: 1000px;
    animation: orbFloat 4s ease-in-out infinite;
  }
  
  .orb {
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, 
      rgba(102, 126, 234, 0.4) 0%, 
      rgba(118, 75, 162, 0.3) 30%,
      rgba(17, 153, 142, 0.2) 60%,
      transparent 70%);
    position: relative;
    animation: orbRotate 20s linear infinite;
    box-shadow: 
      0 0 60px rgba(102, 126, 234, 0.3),
      0 0 100px rgba(118, 75, 162, 0.2),
      inset 0 0 60px rgba(102, 126, 234, 0.1);
  }
  
  .orb::before {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    border: 2px solid rgba(102, 126, 234, 0.3);
    background: 
      radial-gradient(circle at 70% 70%, rgba(240, 147, 251, 0.2), transparent 50%),
      radial-gradient(circle at 30% 30%, rgba(102, 126, 234, 0.3), transparent 50%);
  }
  
  .orb::after {
    content: '';
    position: absolute;
    inset: 20px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.1);
    animation: orbPulse 3s ease-in-out infinite;
  }
  
  .orb-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(102, 126, 234, 0.2);
    animation: orbPulse 3s ease-in-out infinite;
  }
  
  .orb-dots {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-image: 
      radial-gradient(circle, rgba(102, 126, 234, 0.8) 1px, transparent 1px),
      radial-gradient(circle, rgba(118, 75, 162, 0.6) 1px, transparent 1px);
    background-size: 20px 20px, 30px 30px;
    background-position: 0 0, 10px 10px;
    opacity: 0.4;
    animation: orbRotate 30s linear infinite reverse;
  }
`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = orbStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Login failed');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0f1c 0%, #0d1525 50%, #0a0f1c 100%)',
      display: 'flex',
      fontFamily: "'DM Sans', sans-serif"
    }}>
      {/* Left Side - Branding & Orb */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        
        {/* AI Orb */}
        <div className="orb-container" style={{ position: 'relative', marginBottom: '40px' }}>
          <div className="orb">
            <div className="orb-dots" />
            <div className="orb-ring" style={{ inset: '30px' }} />
            <div className="orb-ring" style={{ inset: '50px', animationDelay: '0.5s' }} />
            <div className="orb-ring" style={{ inset: '70px', animationDelay: '1s' }} />
          </div>
        </div>
        
        {/* Branding Text */}
        <h1 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '42px',
          fontWeight: '700',
          color: 'white',
          marginBottom: '12px',
          letterSpacing: '-1px'
        }}>
          BrainForge
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '16px',
          maxWidth: '300px',
          textAlign: 'center',
          lineHeight: '1.6'
        }}>
          Harness the collective intelligence of multiple AI models working together
        </p>
        
        {/* AI Model Pills */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginTop: '30px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {[
            { name: 'Claude', color: '#667eea' },
            { name: 'GPT-4o', color: '#11998e' },
            { name: 'Grok', color: '#eb3349' },
            { name: 'DeepSeek', color: '#f093fb' }
          ].map((ai) => (
            <span key={ai.name} style={{
              padding: '6px 14px',
              borderRadius: '20px',
              background: `${ai.color}20`,
              border: `1px solid ${ai.color}40`,
              color: ai.color,
              fontSize: '13px',
              fontWeight: '500'
            }}>
              {ai.name}
            </span>
          ))}
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: 'rgba(255,255,255,0.03)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(255,255,255,0.08)',
          padding: '48px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '28px',
            fontWeight: '600',
            color: 'white',
            marginBottom: '8px'
          }}>
            Welcome back
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.5)',
            marginBottom: '32px',
            fontSize: '15px'
          }}>
            Sign in to continue to your dashboard
          </p>

          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '24px',
              color: '#f87171',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Password
                </label>
                <Link to="/forgot-password" style={{
                  color: '#667eea',
                  fontSize: '13px',
                  textDecoration: 'none'
                }}>
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'wait' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
              }}
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: '20px', height: '20px' }} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div style={{
            marginTop: '32px',
            textAlign: 'center',
            paddingTop: '24px',
            borderTop: '1px solid rgba(255,255,255,0.08)'
          }}>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
              Don't have an account?{' '}
              <Link to="/signup" style={{
                color: '#667eea',
                fontWeight: '600',
                textDecoration: 'none'
              }}>
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Legal Footer */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '24px'
      }}>
        {['Terms', 'Privacy', 'Refund Policy'].map((item) => (
          <Link
            key={item}
            to={item === 'Refund Policy' ? '/refund' : `/${item.toLowerCase().replace(' ', '-')}`}
            style={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: '12px',
              textDecoration: 'none'
            }}
          >
            {item}
          </Link>
        ))}
      </div>

      {/* Responsive: Hide left panel on mobile */}
      <style>{`
        @media (max-width: 900px) {
          .orb-container { display: none; }
        }
      `}</style>
    </div>
  );
}
